import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { AuditChangeType, AuditFieldChange, AuditLog } from 'common/api/v1/types'
import { tryStringify } from 'common/serialization'

const operationLabels: Record<AuditChangeType, string> = {
    added: 'Added',
    removed: 'Removed',
    changed: 'Changed',
    'sensitive-changed': 'Changed',
}

const humanizeField = (field: string) =>
    field
        .replace(/^\//, '')
        .replaceAll('/', ' › ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, (character) => character.toUpperCase())

type DisplayValue = { kind: 'none' } | { kind: 'text'; text: string } | { kind: 'json'; json: string }
type ResolvedNames = NonNullable<AuditLog['refData']>['names']

const BIGINT_WRAPPER = /^bigint\((-?\d+)\)$/

function stringifyJson(value: unknown): string {
    return JSON.stringify(
        value,
        (_key, nestedValue: unknown) => {
            if (typeof nestedValue === 'bigint') return nestedValue.toString()
            if (typeof nestedValue === 'string') return BIGINT_WRAPPER.exec(nestedValue)?.[1] ?? nestedValue
            return nestedValue
        },
        2
    )
}

function tryParseJsonValue(value: string): string | undefined {
    try {
        const parsed = JSON.parse(value)
        if (parsed !== null && typeof parsed === 'object') return stringifyJson(parsed)
    } catch {
        return undefined
    }
    return undefined
}

function toDisplayValue(value: unknown): DisplayValue {
    if (value === undefined) return { kind: 'none' }
    if (value === null) return { kind: 'text', text: 'null' }
    if (typeof value === 'bigint') return { kind: 'text', text: value.toString() }
    if (typeof value === 'object') {
        try {
            return { kind: 'json', json: stringifyJson(value) }
        } catch {
            return { kind: 'text', text: tryStringify(value) ?? '(complex value)' }
        }
    }
    if (typeof value === 'string') {
        const bigintMatch = BIGINT_WRAPPER.exec(value)
        if (bigintMatch) return { kind: 'text', text: bigintMatch[1] }
        const json = tryParseJsonValue(value)
        return json === undefined ? { kind: 'text', text: value } : { kind: 'json', json }
    }
    return { kind: 'text', text: String(value) }
}

const withName = (value: unknown, names?: ResolvedNames) =>
    typeof value === 'string' && names?.[value] ? `${value} (${names[value]})` : value

const NoneValue = () => (
    <Typography variant="body2" component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        &lt;none&gt;
    </Typography>
)

interface ValueProps {
    renderJson?: (json: string) => ReactNode
    value: unknown
}

const Value = ({ renderJson, value }: ValueProps) => {
    const display = toDisplayValue(value)
    if (display.kind === 'none') return <NoneValue />
    if (display.kind === 'json') {
        if (renderJson) return renderJson(display.json)
        return (
            <Typography
                variant="body2"
                component="pre"
                sx={{ fontFamily: 'monospace', m: 0, overflowX: 'auto', whiteSpace: 'pre' }}
            >
                {display.json}
            </Typography>
        )
    }
    return (
        <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
            {display.text}
        </Typography>
    )
}

interface AuditChangesProps {
    changes?: AuditFieldChange[]
    formatField?: (field: string) => string
    names?: ResolvedNames
    renderJson?: (json: string) => ReactNode
}

export const AuditChanges = ({ changes, formatField = humanizeField, names, renderJson }: AuditChangesProps) => {
    if (!changes?.length) {
        return (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No change details available
            </Typography>
        )
    }

    return (
        <Box
            component="dl"
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 0.7fr 1.5fr 1.5fr',
                columnGap: 3,
                rowGap: 0.75,
                m: 0,
                overflowX: 'auto',
                '& dt, & dd': { m: 0, minWidth: 0, overflowWrap: 'anywhere' },
            }}
        >
            {/* `display: contents` keeps the header cells in the grid while staying legal `<dl>` content. */}
            <Box sx={{ display: 'contents' }}>
                {['Field', 'Operation', 'Previous value', 'New value'].map((label) => (
                    <Typography key={label} variant="caption" color="text.secondary">
                        {label}
                    </Typography>
                ))}
            </Box>
            {[...changes]
                .sort((left, right) => left.field.localeCompare(right.field))
                .map((change) => {
                    const redacted = change.type === 'sensitive-changed'
                    return (
                        <Box key={`${change.field}:${change.type}`} sx={{ display: 'contents' }}>
                            <Typography
                                component="dt"
                                variant="body2"
                                sx={{ fontWeight: 700, fontFamily: 'monospace' }}
                            >
                                {formatField(change.field)}
                            </Typography>
                            <Typography component="dd" variant="body2">
                                {operationLabels[change.type]}
                            </Typography>
                            <Box component="dd">
                                {redacted ? (
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                        &lt;redacted&gt;
                                    </Typography>
                                ) : change.type === 'added' ? (
                                    <NoneValue />
                                ) : (
                                    <Value value={withName(change.from, names)} renderJson={renderJson} />
                                )}
                            </Box>
                            <Box component="dd">
                                {redacted ? (
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                        &lt;redacted&gt;
                                    </Typography>
                                ) : change.type === 'removed' ? (
                                    <NoneValue />
                                ) : (
                                    <Value value={withName(change.to, names)} renderJson={renderJson} />
                                )}
                            </Box>
                        </Box>
                    )
                })}
        </Box>
    )
}
